
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to add Plaid's Income endpoints to your application to obtain income information."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Income to your app' subtitle='Use Income to fetch income information about your users' mdxType="Header" />
    <p>{`This guide will walk you through how to use `}<a parentName="p" {...{
        "href": "/docs/api/products/income/"
      }}>{`Income`}</a>{` to retrieve information about your users' current sources of income.`}</p>
    <Callout mdxType="Callout">
      <p>{`This is a basic quickstart guide that does not cover all features of the Income product. For more complete information about Income endpoints and capabilities, see the individual product pages for `}<a parentName="p" {...{
          "href": "/docs/income/bank-income"
        }}>{`Bank Income`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/income/document-income"
        }}>{`Document Income`}</a>{`, and `}<a parentName="p" {...{
          "href": "/docs/income/payroll-income"
        }}>{`Payroll Income`}</a>{`. `}</p>
      <p>{`The flow below describes the latest integration pattern for Plaid Income. If you are an existing customer who integrated before August 17, 2026, your API calls have a slightly different interface. For details on the differences, see `}<a parentName="p" {...{
          "href": "/docs/api/users/user-apis/"
        }}>{`New User APIs`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard if you wish to retrieve real data in Production. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`, and must be completed before connecting to certain institutions.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-a-user-id"
    }}>{`Create a User ID`}</h4>
    <p>{`Unlike many other Plaid products, where you start by creating `}<inlineCode parentName="p">{`Items`}</inlineCode>{` to represent user connections with individual banks, with Income you first create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` that represents an individual user. As you add various sources of income, those will all be associated with this single `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, which allows you to receive consolidated income from different sources by requesting an income report for that user.`}</p>
    <p>{`To create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` with a unique `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` value representing the current user. Typically, this is the same identifier your application uses for the currently signed-in user. The response returns a Plaid-generated `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` prefixed with `}<inlineCode parentName="p">{`usr_`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`/user/create`}</inlineCode>{` is idempotent. Calling it again with the same `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` returns the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`.`}</p>
    <p>{`Depending on your application, you might create this `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` as soon as your user creates an account, or right before they begin the process of confirming their income data with you.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`// The clientUserId here represents the user's internal user ID with your
// application
const createPlaidUser = async (clientUserId) => {
  const response = await plaidClient.userCreate({
    client_user_id: clientUserId,
  });
  const plaidUserId = response.data.user_id;
  await updateUserRecordWithIncomeInfo(clientUserId, plaidUserId);
};
`}</code></pre>
    <h4 {...{
      "id": "decide-what-income-verification-methods-you-want-to-support"
    }}>{`Decide what income verification method(s) you want to support`}</h4>
    <p>{`Income supports three different ways that a user can verify their sources of income.`}</p>
    <ul>
      <li parentName="ul"><em parentName="li">{`Payroll Income`}</em>{` allows Plaid to retrieve information such as recent pay stubs and W-2 forms directly from the user's payroll provider. This tends to provide the most comprehensive data, but is dependent upon the user being able to sign in with their payroll provider.`}</li>
      <li parentName="ul"><em parentName="li">{`Document Income`}</em>{` allows the user to upload images of income-related documents (such as pay stubs and W-2 forms), which Plaid can scan and interpret for you.`}</li>
      <li parentName="ul"><em parentName="li">{`Bank Income`}</em>{` allows the user to connect with their bank to identify deposits that represent sources of income.`}</li>
    </ul>
    <p>{`Depending on which methods you want to support (and it's perfectly acceptable to support all three), you will need to implement slightly different processes. While you can collect Payroll and Document Income in the same Link flow, you cannot collect Payroll or Document Income in the same Link flow as Bank Income.`}</p>
    <h3 {...{
      "id": "fetching-payroll-or-document-income-data"
    }}>{`Fetching Payroll or Document Income data`}</h3>
    <p>{`The process for implementing Payroll and Document Income is very similar, so we'll cover these two scenarios next.`}</p>
    <h4 {...{
      "id": "create-a-link-token-for-payroll-or-document-income"
    }}>{`Create a Link token for Payroll or Document Income`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant UI flow to guide your users through the process of connecting Plaid with their financial institutions. With Income, Link can help your user search for and connect to their payroll provider as well as assist them in uploading documents.`}</p>
    <p>{`Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the appropriate section in the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, single-use token that is used to authenticate your app with Link. You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <p>{`When calling the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint, you'll include an object telling Plaid about your application as well as how to customize the Link experience. When creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for Payroll or Document Income, there are a few extra values you'll need to supply in this object:`}</p>
    <ul>
      <li parentName="ul">{`You will need to pass in the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` that you created earlier.`}</li>
      <li parentName="ul">{`You can only list `}<inlineCode parentName="li">{`"income_verification"`}</inlineCode>{` in the list of products.`}</li>
      <li parentName="ul">{`You will need to create an object with `}<inlineCode parentName="li">{`income_source_types`}</inlineCode>{` set to `}<inlineCode parentName="li">{`["payroll"]`}</inlineCode>{` and pass that as the value for `}<inlineCode parentName="li">{`income_verification`}</inlineCode></li>
    </ul>
    <p>{`Note that in the code sample below, the top-level `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` is the Plaid-generated identifier returned by `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{`. The `}<inlineCode parentName="p">{`user.client_user_id`}</inlineCode>{` value is your application's identifier for the signed-in user and is primarily used for logging, allowing you to search for activity in the Plaid Dashboard.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`// Using Express

app.post('/api/create_link_token_for_payroll_income', async function (
  request,
  response,
) {
  // Get the client_user_id by searching for the current user.
  const clientUserId = await GetSignedInUserId();
  // Get the Plaid user ID that we stored in an earlier step
  const plaidUserId = await GetPlaidUserIdForUser();
  const configs = {
    user: {
      // This should correspond to a unique id for the current user.
      client_user_id: clientUserId,
    },
    client_name: 'Plaid Test App',
    products: ['income_verification'],
    user_id: plaidUserId,
    income_verification: {
      income_source_types: ['payroll'],
    },
    language: 'en',
    webhook: 'https://webhook.example.com',
    country_codes: ['US'],
  };
  try {
    const createTokenResponse = await client.linkTokenCreate(configs);
    response.json(createTokenResponse.data);
  } catch (error) {
    // handle error
  }
});
`}</code></pre>
    <p>{`By default, a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created this way will allow the user to collect income information using both the Payroll and Document methods. However, in some cases you might want to permit the user to only use one of these methods. To do that, you can specify the `}<inlineCode parentName="p">{`flow_types`}</inlineCode>{`, as in the example below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #Restricting income collection with flow_types",
        "switcher": "false",
        "#Restricting": true,
        "income": true,
        "collection": true,
        "with": true,
        "flow_types": true
      }}>{`income_verification: {
  income_source_types: ["payroll"],
  payroll_income: { flow_types: ["payroll_digital_income"] },
},
`}</code></pre>
    <h5 {...{
      "id": "install-the-link-library"
    }}>{`Install the Link library`}</h5>
    <p>{`You need to install the Link JavaScript library from Plaid in order to use Link in your web application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #index.html",
        "switcher": "false",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <p>{`To run Link, you'll first need to retrieve the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from the server using the call to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` described above. Then, call `}<inlineCode parentName="p">{`Plaid.create()`}</inlineCode>{`, passing along that token alongside other callbacks you might want to define. This will return a handler that has an `}<inlineCode parentName="p">{`open`}</inlineCode>{` method. Call that method to open up the Link UI and start the connection process.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/api/create_link_token_for_payroll_income')).link_token,
  onSuccess: (public_token, metadata) => {
    // Typically, you'd exchange the public_token for an access token.
    // While you can still do that here, it's not strictly necessary.
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
    <p>{`If you've had experience with other Plaid products, you're probably used to taking the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` received from Link and exchanging it on your server for a persistent `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. However, because Payroll and Document Income are fetched once (and don't require persistent connections with your user's bank), and all of your reports are associated with the user's `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, there's no need to retrieve or store an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` in this case.`}</p>
    <h4 {...{
      "id": "listen-for-webhooks"
    }}>{`Listen for webhooks`}</h4>
    <p>{`After a user has finished using Link, it may take some time before Plaid has the user's income information available for you to download. In the case of Payroll Income, this typically takes a few seconds. For Document Income, this typically takes several minutes, and may take up to 45 minutes. Listen for the `}<a parentName="p" {...{
        "href": "/docs/api/products/income/#user_income_verification"
      }}><inlineCode parentName="a">{`INCOME: USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook to know when this user's data is ready.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #webhookServer.js",
        "switcher": "false",
        "#webhookServer.js": true
      }}>{`app.post('/server/receive_webhook', async (req, res, next) => {
  const product = req.body.webhook_type;
  const code = req.body.webhook_code;
  if (product === 'INCOME' && code === 'USER_INCOME_VERIFICATION') {
    const plaidUserId = req.body.user_id;
    const verificationStatus = req.body.verification_status;
    if (verificationStatus === 'VERIFICATION_STATUS_PROCESSING_COMPLETE') {
      await retrieveIncomeDataForUser(plaidUserId);
    } else {
      // Handle other cases
    }
  }
  // Handle other types of webhooks
});
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` value included in this webhook is the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` that Plaid returned when you created the user.`}</p>
    <h4 {...{
      "id": "fetch-income-data"
    }}>{`Fetch income data`}</h4>
    <p>{`Now that Plaid has confirmed it has finished processing this user's data, you can fetch the data by calling `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`. Pass in the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, and Plaid will return all Payroll and Document Income sources associated with that user.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`try {
  const response = await plaidClient.creditPayrollIncomeGet({
    user_id: plaidUserId,
  });
  const incomeData = response.data;
  // Do something interesting with the income data here.
} catch (error) {
  // Handle error
}
`}</code></pre>
    <p>{`This call will typically return an array of `}<inlineCode parentName="p">{`items`}</inlineCode>{`, each of which represents a connection with a payroll provider. These `}<inlineCode parentName="p">{`items`}</inlineCode>{`, in turn, contain one or more `}<inlineCode parentName="p">{`payroll_income`}</inlineCode>{` objects that contain detailed payroll information for the user. This can include individual pay stubs (with a full breakdown of gross pay, deductions, net pay, and more), as well as W-2 forms and all of their information. See the `}<a parentName="p" {...{
        "href": "/docs/api/products/income/#creditpayroll_incomeget"
      }}>{`sample response object`}</a>{` in the documentation for a full example of what this response might look like.`}</p>
    <p>{`You can distinguish between income that was retrieved through Payroll Income and income that was retrieved through Document Income by looking for an `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` value on the `}<inlineCode parentName="p">{`payroll_income`}</inlineCode>{` object. If the data was retrieved through scanning user documents, this value will be null.`}</p>
    <h3 {...{
      "id": "fetching-bank-income-data"
    }}>{`Fetching Bank Income data`}</h3>
    <p>{`As an alternative to downloading income data from the user's payroll provider or W-2 forms, Plaid can also find income data in the user's bank account. Plaid will look for deposits that might be sources of income and ask the user to identify which deposits represent income that they wish to share with your application.`}</p>
    <h4 {...{
      "id": "create-a-link-token-for-bank-income"
    }}>{`Create a Link token for Bank Income`}</h4>
    <p>{`Creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for Bank Income is similar to the process for creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for Payroll or Document Income. The main differences are:`}</p>
    <ul>
      <li parentName="ul">{`While you still must include `}<inlineCode parentName="li">{`"income_verification"`}</inlineCode>{` as a product, you are allowed to combine this product with other products that you might want to use with this financial institution.`}</li>
      <li parentName="ul">{`Your `}<inlineCode parentName="li">{`income_source_types`}</inlineCode>{` must be set to `}<inlineCode parentName="li">{`["bank"]`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Your `}<inlineCode parentName="li">{`income_verification`}</inlineCode>{` object must also include a `}<inlineCode parentName="li">{`bank_income`}</inlineCode>{` object, where you have set the value of `}<inlineCode parentName="li">{`days_requested`}</inlineCode>{`. This is the number of days back that Plaid will search for regularly occurring deposits.`}</li>
    </ul>
    <p>{`The value of `}<inlineCode parentName="p">{`days_requested`}</inlineCode>{` should be a large enough number that you find all relevant deposits, but not so large that the user gets tired waiting for Plaid to retrieve all the relevant data. We recommend 120 days as a good balance between the two.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`app.post('/api/create_link_token_for_bank_income', async function (
  request,
  response,
) {
  // Get the client_user_id by searching for the current user.
  const clientUserId = await GetSignedInUserId();
  // Get the Plaid user ID that we stored in an earlier step
  const plaidUserId = await GetPlaidUserIdForUser();
  const configs = {
    user: {
      // This should correspond to a unique id for the current user.
      client_user_id: clientUserId,
    },
    client_name: 'Plaid Test App',
    products: ['income_verification', 'assets'],
    user_id: plaidUserId,
    income_verification: {
      income_source_types: ['bank'],
      bank_income: { days_requested: 120 },
    },
    language: 'en',
    webhook: 'https://webhook.example.com',
    country_codes: ['US'],
  };
  try {
    const createTokenResponse = await client.linkTokenCreate(configs);
    response.json(createTokenResponse.data);
  } catch (error) {
    // handle error
  }
});
`}</code></pre>
    <h5 {...{
      "id": "install-the-link-library-1"
    }}>{`Install the Link library`}</h5>
    <p>{`As described earlier, you need to install the Link JavaScript library from Plaid to use Link in your web application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler-1"
    }}>{`Configure the client-side Link handler`}</h5>
    <p>{`Retrieve the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from the server, then call `}<inlineCode parentName="p">{`Plaid.create()`}</inlineCode>{`, passing along the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` alongside other callbacks you might want to define. This will return a handler that has an `}<inlineCode parentName="p">{`open`}</inlineCode>{` method, which you can call to start the Link process. This is similar to the process described above, but with Bank Income you will probably want to keep the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and exchange it for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, as described in the next step.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/api/create_link_token_for_bank_income')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
    <h5 {...{
      "id": "retrieve-metadata-about-the-link-session"
    }}>{`Retrieve metadata about the Link session`}</h5>
    <p>{`Call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` in your server after receiving `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`, passing in the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. If you enabled `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link`}</a>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` will not contain a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`; get it instead from the `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook or from `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link docs`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`Previously, customers were instructed to call `}<inlineCode parentName="p">{`/credit/sessions/get`}</inlineCode>{` to obtain the public token. While this flow is still supported, all customers are encouraged to use the `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` endpoint to obtain the public token instead, for improved consistency with other Plaid products and flows.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`try {
  const response = await plaidClient.linkTokenGet({
    link_token: LinkToken,
  });
  const sessionData = response.data;
} catch (error) {
  // Handle this error
}
`}</code></pre>
    <p>{`If you are using other Plaid products such as Auth or Balance alongside Bank Income, make sure to capture the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` (or tokens) from the `}<inlineCode parentName="p">{`results.item_add_results`}</inlineCode>{` field and exchange it for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "get-a-persistent-access-token-for-other-products"
    }}>{`Get a persistent access token (for other products)`}</h4>
    <p>{`For the purpose of retrieving income data, you don't necessarily need an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. Plaid will retrieve the income data for you, and then that data will be associated with the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` that you created when first configuring Link.`}</p>
    <p>{`However, if you are using any other product with this financial institution (such as Transactions, Balance, or Assets), you will need to exchange your `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` lets you make authenticated calls to these other products in the Plaid API.`}</p>
    <p>{`Exchanging a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from the server-side handler. Store the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` that get returned in a secure server-side location. Never store this information on the client.`}</p>
    <SampleCode.ExchangePublicToken />
    <h4 {...{
      "id": "fetch-income-data-1"
    }}>{`Fetch income data`}</h4>
    <p>{`You can fetch income data by calling the `}<inlineCode parentName="p">{`/credit/bank_income/get`}</inlineCode>{` endpoint using the Plaid client library, passing in the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` you created earlier. Note that, similar to Document or Payroll Income, this data is static -- it is a snapshot of the user's income data that was retrieved by Plaid when the user ran Link. It does not update or change over time.`}</p>
    <p>{`By default, Plaid will only return the most recent report in its response. However, you might want to retrieve more than one report if your user has intentionally run Link multiple times. You can do this by passing an `}<inlineCode parentName="p">{`options`}</inlineCode>{` object with a `}<inlineCode parentName="p">{`count`}</inlineCode>{` value set to an integer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #server.js",
        "switcher": "false",
        "#server.js": true
      }}>{`try {
  const response = await plaidClient.creditBankIncomeGet({
    user_id: userRecord[FIELD_PLAID_USER_ID],
    options: {
      count: 3,
    },
  });
  const bankIncomeData = response.data;
} catch (error) {
  // Handle this error
}
`}</code></pre>
    <p>{`This call typically returns a `}<inlineCode parentName="p">{`bank_income`}</inlineCode>{` array, which consists of a number of different objects that represent the report generated when the user ran Link. These individual reports contain an `}<inlineCode parentName="p">{`items`}</inlineCode>{` array, which in turn contains one or more objects that represent the financial institutions associated with this report. These objects contain a `}<inlineCode parentName="p">{`bank_income_accounts`}</inlineCode>{` array of objects, which gives you information about the individual accounts, as well as a `}<inlineCode parentName="p">{`bank_income_sources`}</inlineCode>{` array of objects, which in turn contains the total amount of income reported, along with historical transactions about that income.`}</p>
    <p>{`Each report also contains a `}<inlineCode parentName="p">{`bank_income_summary`}</inlineCode>{` object, that summarizes the total bank income identified across all items in this report.`}</p>
    <h4 {...{
      "id": "example-code"
    }}>{`Example code`}</h4>
    <p>{`For an example of an app that incorporates Document, Payroll and Bank Income, see the React and Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/income-sample"
      }}>{`Income Sample app`}</a>{`. The Income Sample app is an application that uses Income and Liability data to help determine whether the user can qualify for financing on a pre-owned hoverboard. It supports the use of all three types of income data.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-a-user-id",
        level: 4,
        title: "Create a User ID",
      },
{
        id: "decide-what-income-verification-methods-you-want-to-support",
        level: 4,
        title: "Decide what income verification method(s) you want to support",
      },
{
        id: "fetching-payroll-or-document-income-data",
        level: 3,
        title: "Fetching Payroll or Document Income data",
      },
{
        id: "create-a-link-token-for-payroll-or-document-income",
        level: 4,
        title: "Create a Link token for Payroll or Document Income",
      },
{
        id: "listen-for-webhooks",
        level: 4,
        title: "Listen for webhooks",
      },
{
        id: "fetch-income-data",
        level: 4,
        title: "Fetch income data",
      },
{
        id: "fetching-bank-income-data",
        level: 3,
        title: "Fetching Bank Income data",
      },
{
        id: "create-a-link-token-for-bank-income",
        level: 4,
        title: "Create a Link token for Bank Income",
      },
{
        id: "get-a-persistent-access-token-for-other-products",
        level: 4,
        title: "Get a persistent access token (for other products)",
      },
{
        id: "fetch-income-data-1",
        level: 4,
        title: "Fetch income data",
      },
{
        id: "example-code",
        level: 4,
        title: "Example code",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
